import { useState } from 'react';
import {
  fill, formatPhone, personTextsPath, Size, t,
  type DateFormat, type PersonTextsAnswer, type TextThreadRow,
} from '@bms2/shared';
import { Button, Textarea } from '../../components/controls';
import { LoadFailure } from '../../components/composites';
import { post, useApi } from './api';
import { TextThreadList } from './TextThreadList';
import styles from './PersonTextsPanel.module.css';

/**
 * 🔒 The Person page's Texts tile (piece 13; decision 5210): the Person's
 * texts on the company number, oldest first, an outbound one at the right
 * with its status word, then a box and Send at the foot. 🔨 It reads its own
 * route, so the record page's one answer stays the size it was.
 */
export function PersonTextsPanel({ personId, zone, dateFormat, apiBaseUrl = '' }: { personId: string; zone: string; dateFormat: DateFormat; apiBaseUrl?: string }) {
  const path = personTextsPath(personId);
  const fetched = useApi<PersonTextsAnswer>(path, apiBaseUrl);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  if (fetched.state.kind === 'loading') return <p className={styles.quiet}>{t('async.loading')}</p>;
  if (fetched.state.kind === 'failed') return <LoadFailure state={fetched.state} onRetry={fetched.reload} />;
  if (fetched.state.kind !== 'ready') return null;
  const answer = fetched.state.data;
  const boxLabel = fill(t('textThread.boxTo'), { number: formatPhone(answer.lines[0] ?? '') });

  const send = async () => {
    setBusy(true);
    setRefusal(null);
    const sent = await post<TextThreadRow>(path, { body: draft, after: answer.newestId }, apiBaseUrl);
    setBusy(false);
    if (!sent.ok) {
      setRefusal(`${t('textThread.notSent')} ${sent.message}`);
      return;
    }
    setDraft('');
    fetched.reload();
  };

  return (
    <div className={styles.panel} data-person-texts={personId}>
      {answer.rows.length === 0 ? <p className={styles.quiet}>{t('textThread.empty')}</p> : null}
      {answer.rows.length > 0 ? (
        <TextThreadList rows={answer.rows} zone={zone} dateFormat={dateFormat} />
      ) : null}
      {answer.lines.length === 0 ? <p className={styles.quiet}>{t('textThread.noLine')}</p> : null}
      {answer.sends ? (
        <div className={styles.compose}>
          <Textarea size={Size.Large} label={boxLabel} labelHidden value={draft} onChange={setDraft} placeholder={t('textThread.placeholder')} rows={2} disabled={busy} name={`person-text-${personId}`} />
          <Button size={Size.Small} text={[busy ? t('textThread.sending') : t('textThread.send')]} actions={[() => void send()]} disabled={busy || draft.trim() === ''} />
        </div>
      ) : null}
      {refusal ? <p className={styles.failed} role="alert">{refusal}</p> : null}
    </div>
  );
}
